import React, { useEffect, useState } from "react";
import SelectInput from "./SelectInput";
import { OrganizationUserDto } from "./ObjectTypes";
import { ActionTypes } from "./Enums";
import ConfirmationModal from "./ConfirmationModal";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";
import { BeatLoader } from "react-spinners";
import RolePermissionsInfo from "./RolePermissionsInfo";
import { RoleScope } from "./rolePermissions";

interface UserActionsModalProps {
  selectedUser: OrganizationUserDto | undefined; // Allow undefined
  isOpen: boolean;
  onClose: () => void;
  roles: any[];
  /**
   * Which role set is on offer. `entityName` is a display string and cannot
   * stand in for this, so each users tab states it.
   */
  scope: RoleScope;
  onSend: (action_id: string, role_id: string) => void;
  perms: any[];
  entityName: string;
  isSending: boolean;
}

const UserActionsModal: React.FC<UserActionsModalProps> = ({
  selectedUser,
  isOpen,
  onClose,
  roles,
  scope,
  onSend,
  perms,
  entityName,
  isSending,
}) => {
  const [selectedAction, setSelectedAction] = useState("");
  const [defaultRole, setDefaultRole] = useState<any>();
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [actions, setActions] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>();
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedUser) {
      const matchedRole = roles.find(
        (role) => role.value === selectedUser.role_id,
      );
      if (matchedRole) {
        setDefaultRole({
          label: t(matchedRole.name),
          value: matchedRole.value,
        });
        setSelectedRole({
          label: t(matchedRole.name),
          value: matchedRole.value,
        });
      }
    }
  }, [selectedUser, roles, t]);

  useEffect(() => {
    let tempActions = [];
    if (perms) {
      if (
        perms.filter((perm: any) => perm.name === "CHANGE_USER_ROLE").length > 0
      ) {
        tempActions.push({
          label: t("Change Role"),
          value: ActionTypes.CHANGE_ROLE,
        });
        tempActions.push({
          label: t("Remove User"),
          value: ActionTypes.REMOVE_USER,
        });
      }
      if (perms.filter((perm: any) => perm.name.includes("INVITE")).length > 0)
        tempActions.push({
          label: t("Resend Invitation"),
          value: ActionTypes.RESEND_INVITATION,
        });
      setActions(tempActions);
    }
  }, [perms, t]);

  if (!isOpen) return null;

  const handleAction = (action: any) => {
    if (action === null) {
      setSelectedAction("");
      return;
    }
    setSelectedAction(action.value);
  };

  const handleChangeRole = (role: any) => {
    if (role === null) {
      setSelectedRole(null);
      return;
    }
    if (selectedRole === role) {
      return;
    }
    setSelectedRole(role);
  };

  const handleOnClose = () => {
    setSelectedAction("");
    onClose();
  };

  const handleActionSend = () => {
    if (selectedAction === ActionTypes.CHANGE_ROLE) {
      setConfirmationMessage(
        `${t("Are you sure you want to change the role of")} ${
          selectedUser?.first_name
        } ${selectedUser?.last_name} ${t("to")} ${selectedRole.label}?`,
      );
    } else if (selectedAction === ActionTypes.RESEND_INVITATION) {
      // Don't need confirmation for this action
      handleConfirmation();
      return;
    } else if (selectedAction === ActionTypes.REMOVE_USER) {
      setConfirmationMessage(
        `${t("Are you sure you want to remove the user: ")} ${
          selectedUser?.first_name
        } ${selectedUser?.last_name} ${t("from")} ${entityName}?`,
      );
    }
    setDisplayConfirmationModal(true);
  };

  const handleConfirmation = () => {
    setDisplayConfirmationModal(false);
    onSend(selectedAction, selectedRole?.value);
    setSelectedAction("");
    setSelectedRole(null);
  };

  return (
    <div className={`${tailwindClassnames.modalOverlay} z-10`}>
      <div className={`${tailwindClassnames.modalPanel} max-w-md`}>
        {/* Nested confirm renders its own z-50 overlay above this modal. */}
        <ConfirmationModal
          isOpen={displayConfirmationModal}
          confirmationMessage={confirmationMessage}
          onConfirm={handleConfirmation}
          onCancel={() => setDisplayConfirmationModal(false)}
          danger={selectedAction === ActionTypes.REMOVE_USER}
        />
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 className={tailwindClassnames.modalTitle}>
            {t("Actions for")} {selectedUser?.first_name}{" "}
            {selectedUser?.last_name}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={handleOnClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>
        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          <SelectInput
            className={"w-full mr-1"}
            defaultValue={undefined}
            isDisabled={displayConfirmationModal}
            isLoading={false}
            isClearable={true}
            isSearchable={true}
            isMulti={false}
            name={t("Actions")}
            errors={""}
            options={actions}
            onChange={handleAction}
            searchBar={false}
          />
          {selectedUser && selectedAction === ActionTypes.CHANGE_ROLE && (
            <SelectInput
              options={roles.map((item) => ({
                label: t(item.name),
                value: item.value,
              }))}
              className={"w-48 mr-1"}
              defaultValue={defaultRole ? defaultRole : undefined}
              isDisabled={displayConfirmationModal}
              isLoading={false}
              isClearable={true}
              isSearchable={true}
              isMulti={false}
              name={t("Roles")}
              labelSuffix={<RolePermissionsInfo scope={scope} />}
              errors={""}
              onChange={handleChangeRole}
              searchBar={false}
            />
          )}
        </div>
        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            onClick={handleOnClose}
            className={tailwindClassnames.btnSecondary}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={() => handleActionSend()}
            disabled={
              isSending ||
              !selectedAction ||
              (selectedAction === ActionTypes.CHANGE_ROLE && !selectedRole) ||
              (selectedAction === ActionTypes.CHANGE_ROLE &&
                selectedRole.value === selectedUser?.role_id)
            }
            className={`relative ${tailwindClassnames.btnPrimary}`}
          >
            {/* Label stays in layout while sending so the button keeps its
                width; currentColor spinner reads on the button fill in both
                themes. */}
            <span className={isSending ? "invisible" : ""}>{t("Send")}</span>
            {isSending && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserActionsModal;
